import React, { useState, useEffect, useMemo } from "react";
import Sidebar from "../components/Sidebar";
import Card from "../components/Card";
import DateSearch from "../components/DateSearch";
import LeadTable from "../components/LeadTable";
import LedgerTable from "../components/LedgerTable";
import AttendancePage from "./AttendancePage";
import ExcelExport from "../components/ExcelExport";
import QuotationModal from "../components/QuotationModal";
import CallRegister from "../components/CallRegister";
import GoldUserModal from "../components/goldUserModal";
import { Bars3Icon } from "@heroicons/react/24/outline";
import { SERVER_URL } from "../config";
import axios from 'axios';

/* ── stat card ──────────────────────────────────────────────────────────────── */
const StatCard = ({ title, count, color }) => {
  const themes = {
    indigo: {
      bg: "bg-white border border-slate-200",
      accent: "text-blue-700",
      val: "text-slate-800",
      dot: "bg-blue-500",
      bar: "from-blue-500 to-blue-600",
    },
    emerald: {
      bg: "bg-white border border-slate-200",
      accent: "text-emerald-700",
      val: "text-slate-800",
      dot: "bg-emerald-500",
      bar: "from-emerald-500 to-emerald-600",
    },
    rose: {
      bg: "bg-white border border-slate-200",
      accent: "text-rose-700",
      val: "text-slate-800",
      dot: "bg-rose-500",
      bar: "from-rose-500 to-rose-600",
    },
    amber: {
      bg: "bg-white border border-slate-200",
      accent: "text-amber-700",
      val: "text-slate-800",
      dot: "bg-amber-500",
      bar: "from-amber-500 to-amber-600",
    },
  };
  const t = themes[color] || themes.indigo;
  return (
    <div
      className={`${t.bg} rounded-xl px-5 py-4 flex flex-col gap-2`}
      style={{ boxShadow: "0 1px 6px rgba(15,23,42,0.06)" }}
    >
      <div className="flex items-center gap-2">
        <span className={`w-2.5 h-2.5 rounded-full ${t.dot}`} />
        <p className={`text-[10px] font-bold uppercase tracking-widest ${t.accent}`}>{title}</p>
      </div>
      <p className={`text-3xl font-extrabold ${t.val}`}>{count}</p>
      <div className="w-full h-1 rounded-full bg-slate-100 overflow-hidden">
        <div className={`h-full rounded-full bg-gradient-to-r ${t.bar}`} style={{ width: "60%" }} />
      </div>
    </div>
  );
};

/* ════════════════════════════════════════════════════════════════════════════ */
const DashboardPage = ({ user, onLogout, onNavigate, activePage }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [leads, setLeads] = useState([]);
  const [counts, setCounts] = useState({});
  const [limit, setLimit] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [goldUserOpen, setGoldUserOpen] = useState(false);
  const [quotationOpen, setQuotationOpen] = useState(false);
  const [dateFilter, setDateFilter] = useState({ start: "", end: "" });
  const [showCallRegister, setShowCallRegister] = useState(false);

  /* ── ledger table (opened from the action bar, alongside Call Register / Quotation) ── */
  const [showLedgerTable, setShowLedgerTable] = useState(false);
  const [ledgers, setLedgers] = useState([]);
  const [ledgerLimit, setLedgerLimit] = useState(10);
  const [ledgerPage, setLedgerPage] = useState(1);
  const [ledgerSearchQuery, setLedgerSearchQuery] = useState("");
  const [fullyFilteredLedgers, setFullyFilteredLedgers] = useState([]);

  const dateFilteredLeads = useMemo(() => {
    if (!dateFilter.start || !dateFilter.end) return leads;
    return leads.filter((lead) => {
      if (!lead.leadCreatedDate) return false;
      const leadDate = String(lead.leadCreatedDate).split(" ")[0];
      return leadDate >= dateFilter.start && leadDate <= dateFilter.end;
    });
  }, [leads, dateFilter]);

  const [fullyFilteredLeads, setFullyFilteredLeads] = useState([]);

  useEffect(() => {
    setFullyFilteredLeads(dateFilteredLeads);
    setCurrentPage(1);
  }, [dateFilteredLeads]);

  const leadStats = useMemo(() => {
    let total = fullyFilteredLeads.length;
    let active = 0, successful = 0, closed = 0;
    fullyFilteredLeads.forEach((lead) => {
      const status = (lead.prospect || "").toLowerCase();
      if (status === '0'|| status ===  '1' || status === '2' || status === '3' || status === '4' || status === '5') active++;
      if (status === '6') successful++;
      if (status === '7' || status === '8') closed++;
    });
    return { total, active, successful, closed };
  }, [fullyFilteredLeads]);

  const employee = JSON.parse(localStorage.getItem("employee") || "{}");

  const fetchLeads = async () => {
    try {
      const payload = { userName: employee.employee_name, userId: employee.employee_id };
      const response = await axios.post(`${SERVER_URL}/DataFetch/leadHistory.php`, payload);
      if (response.data.status === "success") {
        setLeads(response.data.leads);
        setCounts(prev => ({ ...prev, total: response.data.leads.length }));
      }
    } catch (err) {
      console.error("Network error:", err);
    }
  };

  /* ── fetch ledgers for the Ledgers overlay ─────────────────────────────── */
  const fetchLedgers = async () => {
    try {
        const response = await axios.get(`${SERVER_URL}/Rahul/fetchLedgers.php`);

        console.log("Status:", response.status);
        console.log("Headers:", response.headers);
        console.log("Data:", response.data);

        setLedgers(response.data.ledgers || []);
    } catch (err) {
        console.log(err);

        if (err.response) {
            console.log("Response:", err.response.data);
        }
    }
};

  const handleOpenLedgerTable = () => {
    setShowLedgerTable(true);
    fetchLedgers();
  };

  useEffect(() => { fetchLeads(); }, []);
  useEffect(() => { if (activePage === "dashboard") fetchLeads(); }, [activePage]);

  const handleSearch = (q) => { setSearchQuery(q || ""); setCurrentPage(1); };
  const handleDateSearch = (start, end, isDateFiltering = true) => {
    if (isDateFiltering) setDateFilter({ start, end });
    else setDateFilter({ start: "", end: "" });
    setCurrentPage(1);
  };
  const handleLimitChange = (newLimit) => { setLimit(newLimit); setCurrentPage(1); };
  const handlePageChange = (page) => {
    if (page < 1 || (totalPages && page > totalPages)) return;
    setCurrentPage(page);
  };

  const handleLedgerSearch = (q) => { setLedgerSearchQuery(q || ""); setLedgerPage(1); };
  const handleLedgerLimitChange = (newLimit) => { setLedgerLimit(newLimit); setLedgerPage(1); };
  const handleLedgerPageChange = (page) => {
    const totalLedgerPages = Math.ceil(fullyFilteredLedgers.length / ledgerLimit) || 1;
    if (page < 1 || page > totalLedgerPages) return;
    setLedgerPage(page);
  };

  const excelColumns = [
    { header: "Employee Name", key: "username", type: "text" },
    { header: "Employee ID", key: "user_id", type: "text" },
    { header: "Branch", key: "branch", type: "text" },
    { header: "Creation Date", key: "leadCreatedDate", type: "text" },
    { header: "Client Name", key: "companyName", type: "text" },
    { header: "Contact Person", key: "contactPerson", type: "text" },
    { header: "Mobile Number", key: "mobileNumber", type: "text" },
    { header: "Mail ID", key: "mailId", type: "text" },
    { header: "Address", key: "address", type: "text" },
    { header: "Pincode", key: "pincode", type: "text" },
    { header: "Tally product", key: "products", type: "text" },
    { header: "Busy product", key: "busy", type: "text" },
    { header: "Option", key: "options", type: "text" },
    { header: "Remarks", key: "remarks", type: "text" },
  ];

  const renderPageContent = () => {
    if (activePage === "attendance") return <AttendancePage />;

    if (activePage === "admin") {
      return (
        <div
          className="flex items-center justify-center h-64 rounded-xl border border-slate-200 bg-white"
          style={{ boxShadow: "0 1px 6px rgba(5, 5, 5, 0.06)" }}
        />
      );
    }

    return (
      <div className="flex flex-col gap-1">

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard title="Total Leads" count={leadStats.total} color="indigo" />
          <StatCard title="Active Leads" count={leadStats.active} color="amber" />
          <StatCard title="Successful Leads" count={leadStats.successful} color="emerald" />
          <StatCard title="Closed Leads" count={leadStats.closed} color="rose" />
        </div>

        {/* Filter bar */}
        <div
          className="px-0.5 py-0.5 rounded-xl border border-slate-200 bg-white"
          style={{ boxShadow: "0 1px 6px rgba(15,23,42,0.06)" }}
        >
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-1">
            <DateSearch onSearch={handleDateSearch} />
            <div className="flex items-center gap-1 flex-wrap justify-end">
              <button
                onClick={() => setShowCallRegister(true)}
                className="px-1 py-1 rounded-lg text-white font-semibold text-sm transition-colors hover:opacity-90"
                style={{
                  background: "linear-gradient(90deg,#7c3aed,#5b21b6)",
                  boxShadow: "0 2px 2px rgba(124,58,237,0.18)",
                }}
              >
                📞 Register
              </button>
              <button
                onClick={() => setQuotationOpen(true)}
                className="px-1 py-1 rounded-lg text-white font-semibold text-sm transition-colors hover:opacity-90"
                style={{
                  background: "linear-gradient(90deg,#1e40af,#0369a1)",
                  boxShadow: "0 2px 2px rgba(30,64,175,0.18)",
                }}
              >
                📄Quotation
              </button>
              <button
                onClick={handleOpenLedgerTable}
                className="px-1 py-1 rounded-lg text-white font-semibold text-sm transition-colors hover:opacity-90"
                style={{
                  background: "linear-gradient(90deg,#0f766e,#0e7490)",
                  boxShadow: "0 2px 2px rgba(15,118,110,0.18)",
                }}
              >
                📒 Ledgers
              </button>
              <ExcelExport
                data={fullyFilteredLeads}
                columns={excelColumns}
                fileName="LeadReport.xlsx"
                reportTitle="Lead Report"
                reportFilters={{ fromDate: dateFilter.start, toDate: dateFilter.end }}
              />
              {/* <button
                onClick={() => setGoldUserOpen(true)}
                className="px-3 py-2 rounded-lg text-white font-semibold text-sm transition-colors hover:opacity-90"
                style={{
                  background: "linear-gradient(90deg,#059669,#10b981)",
                  boxShadow: "0 2px 2px rgba(5,150,105,0.18)",
                }}
              >
                🪙 Gold Users
              </button> */}
            </div>
          </div>
        </div>

        {/* Lead Table */}
        <div
          className="rounded-xl overflow-hidden border border-slate-200 bg-white"
          style={{ boxShadow: "0 1px 6px rgba(15,23,42,0.06)" }}
        >
          <LeadTable
            leads={dateFilteredLeads}
            totalEntries={dateFilteredLeads.length}
            currentPage={currentPage}
            totalPages={totalPages}
            limit={limit}
            onPageChange={handlePageChange}
            onSearch={handleSearch}
            onLimitChange={handleLimitChange}
            searchQuery={searchQuery}
            onFilteredDataChange={(data) => setFullyFilteredLeads(data)}
          />
        </div>

      </div>
    );
  };

  /* ════════════════════════════════ JSX ════════════════════════════════ */
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
        * { box-sizing: border-box; }
        body { font-family: 'Inter', sans-serif; }
        .att-scrollbar::-webkit-scrollbar { width: 5px; height: 5px; }
        .att-scrollbar::-webkit-scrollbar-track { background: #f1f5f9; border-radius: 10px; }
        .att-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
        .att-scrollbar::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
      `}</style>

      <div className="flex h-screen overflow-hidden font-[Inter]" style={{ background: "#f0f4f8" }}>

        {/* Top accent line */}
        <div className="fixed top-0 left-0 right-0 h-0.5 z-50"
          style={{ background: "linear-gradient(90deg, #1e40af 0%, #0369a1 50%, #0e7490 100%)" }} />

        <Sidebar
          onLogout={onLogout}
          isOpen={mobileMenuOpen}
          onClose={() => setMobileMenuOpen(false)}
          onNavigate={onNavigate}
        />

        <div className="flex-1 flex flex-col overflow-hidden relative z-10">

          {/* Sticky header */}
          <header
            className="flex-shrink-0 sticky top-0 z-30 border-b border-slate-200"
            style={{ background: "#ffffff", boxShadow: "0 1px 4px rgba(15,23,42,0.08)" }}
          >
            <div className="px-3.5 py-1.5 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <button
                  className="p-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-600 transition-colors border-0"
                  onClick={() => setMobileMenuOpen(true)}
                >
                  <Bars3Icon className="w-5 h-5" />
                </button>
                <div className="flex items-center gap-2.5">
                  <div className="w-1 h-6 rounded-full"
                    style={{ background: "linear-gradient(180deg, #1e40af, #0369a1)" }} />
                  <h1 className="text-lg font-bold tracking-tight text-slate-800">
                    CRM Dashboard
                  </h1>
                </div>
              </div>
            </div>
          </header>
          <main className="flex-1 overflow-y-auto p-2">
            {renderPageContent()}
          </main>
        </div>

        <QuotationModal isOpen={quotationOpen} onClose={() => setQuotationOpen(false)} />
        <GoldUserModal open={goldUserOpen} onClose={() => setGoldUserOpen(false)} />
        {showCallRegister && (
          <CallRegister onClose={() => setShowCallRegister(false)} />
        )}
        {showLedgerTable && (
          <LedgerTable
            ledgers={ledgers}
            currentPage={ledgerPage}
            limit={ledgerLimit}
            onPageChange={handleLedgerPageChange}
            onLimitChange={handleLedgerLimitChange}
            searchQuery={ledgerSearchQuery}
            onFilteredDataChange={(data) => setFullyFilteredLedgers(data)}
            onRefresh={fetchLedgers}
            onClose={() => setShowLedgerTable(false)}
          />
        )}
      </div>
    </>
  );
};

export default DashboardPage;